import { useRef, useState } from "react";

import { CloseIcon, DefaultImageIcon, TrashIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useOnClickOutside from "@/hooks/ui/useClickOutside";
import {
  hexToRgb,
  hslToRgb,
  hsvToRgb,
  normalizeHex,
  Rgb,
  rgbToHsl,
  rgbToHsv
} from "@/utils/dataset";

import {
  hexToHsva,
  type HsvaColor,
  hsvaToHex,
  rgbToHex
} from "@uiw/color-convert";
import Hue from "@uiw/react-color-hue";
import Saturation from "@uiw/react-color-saturation";
import { twMerge } from "tailwind-merge";

// Dismiss on press, not release, so a saturation/hue drag that starts inside
// the panel and ends outside it doesn't close the picker mid-pick.
const DISMISS_EVENTS = ["mousedown", "touchstart"];

const ACCEPTED_IMAGE_TYPES = "image/svg+xml,image/png,image/jpeg";
const MAX_IMAGE_SIZE_BYTES = 5 * 1024;
const CANVAS_HEIGHT = 150;

type ColorTab = "hex" | "rgb" | "hsl" | "hsb";
const COLOR_TABS: { value: ColorTab; label: string }[] = [
  { value: "hex", label: "Hex" },
  { value: "rgb", label: "RGB" },
  { value: "hsl", label: "HSL" },
  { value: "hsb", label: "HSB" }
];

interface ColorPickerPanelProps {
  mode: "color" | "image";
  onClose: () => void;
  // Color mode
  color?: string;
  onColorChange?: (hex: string) => void;
  // Image mode
  onImageSelected?: (dataUrl: string, fileName: string) => void;
  /** Already-applied image, shown pre-loaded when reopening the panel. */
  initialImage?: { dataUrl: string; name: string } | null;
}

/** A labelled numeric input used by the RGB/HSL/HSB tabs. */
const ChannelInput = ({
  label,
  value,
  max,
  onChange
}: {
  label: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
}) => (
  <label className="flex flex-1 flex-col gap-1">
    <span className="text-grey text-[10px] uppercase tracking-wide">
      {label}
    </span>
    <input
      type="number"
      min={0}
      max={max}
      value={value}
      onChange={(e) => {
        const next = Number(e.currentTarget.value);
        if (!Number.isNaN(next)) onChange(next);
      }}
      className="w-full rounded-md border border-gainsboro px-2 py-1 text-xs text-txtHeading outline-none focus:border-primary-teal"
      data-testid={`color-input-${label.toLowerCase()}`}
    />
  </label>
);

/** Controlled hex text input — value comes straight from the `color` prop and
 *  valid `#rrggbb` edits are emitted to the parent via `onChange`. */
const HexInput = ({
  color,
  onChange
}: {
  color: string;
  onChange: (hex: string) => void;
}) => (
  <input
    value={color}
    onChange={(e) => {
      const normalized = normalizeHex(e.currentTarget.value);
      if (hexToRgb(normalized)) onChange(normalized);
    }}
    className="w-full rounded-md border border-gainsboro px-2 py-1 text-xs uppercase text-txtHeading outline-none focus:border-primary-teal"
    data-testid="hex-input"
  />
);

const ColorPickerPanel = ({
  mode,
  onClose,
  color = "#000000",
  onColorChange,
  onImageSelected,
  initialImage = null
}: ColorPickerPanelProps) => {
  const panelRef = useRef(null);
  useOnClickOutside(panelRef, onClose, [], true, DISMISS_EVENTS);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<ColorTab>("hex");

  // The canvas/hue are derived straight from the `color` prop. Hue is the one
  // piece the hex can't always carry — achromatic colors (black/white) collapse
  // it to 0 — so we keep the last chromatic hue locally as a fallback.
  const incoming = hexToHsva(color);
  const isAchromatic = incoming.s === 0 || incoming.v === 0;
  const [hue, setHue] = useState(incoming.h);
  if (!isAchromatic && incoming.h !== hue) setHue(incoming.h);
  const hsva: HsvaColor = { ...incoming, h: isAchromatic ? hue : incoming.h };

  // Image-mode local state — pre-loaded with the already-applied image so
  // reopening the panel shows the current image rather than an empty dropzone.
  const [pendingImage, setPendingImage] = useState<{
    dataUrl: string;
    name: string;
  } | null>(initialImage);
  const [imageError, setImageError] = useState<string | null>(null);

  // Edits flow up to the parent through `onColorChange`; we remember the hue so
  // it survives a trip through black/white where the hex would reset it to 0.
  const emitHsva = (next: HsvaColor) => {
    setHue(next.h);
    onColorChange?.(hsvaToHex(next));
  };

  const rgb = hexToRgb(color) ?? { r: 0, g: 0, b: 0 };
  const hsl = rgbToHsl(rgb);
  const hsv = rgbToHsv(rgb);

  const emitRgb = (next: Rgb) => onColorChange?.(rgbToHex(next));

  const handleSelectFile = (file?: File) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setImageError("Image must be 5KB or smaller.");
      setPendingImage(null);
      return;
    }
    setImageError(null);
    const reader = new FileReader();
    reader.onload = () =>
      setPendingImage({ dataUrl: reader.result as string, name: file.name });
    reader.readAsDataURL(file);
  };

  const handleRemovePendingImage = () => {
    setPendingImage(null);
    setImageError(null);
  };

  const handleUpload = () => {
    if (!pendingImage) return;
    onImageSelected?.(pendingImage.dataUrl, pendingImage.name);
    onClose();
  };

  return (
    <div
      ref={panelRef}
      data-testid="color-picker-panel"
      className={twMerge(
        "z-[99] font-sans border border-gainsboro bg-white rounded-lg w-[338px]",
        "flex flex-col py-[16px] fixed left-[430px] top-[30px] text-xs"
      )}
    >
      {/* Header */}
      <div className="flex flex-row items-start justify-between px-4">
        <h2 className="text-txtHeading text-[20px] font-bold">
          {mode === "image" ? "Upload Image" : "Color picker"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close color picker"
          data-testid="color-picker-close"
          className="border-0 bg-transparent p-0"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>

      <div className="mt-3 flex flex-col gap-3 px-4">
        {mode === "image" ? (
          <>
            {pendingImage ? (
              /* Uploaded: show the image with a trash control to remove it */
              <div
                className="relative flex items-center justify-center rounded-lg border border-dashed border-gainsboro px-4 py-6"
                data-testid="image-preview"
              >
                <img
                  src={pendingImage.dataUrl}
                  alt={pendingImage.name}
                  className="size-16 rounded-xl object-cover"
                />
                <button
                  type="button"
                  onClick={handleRemovePendingImage}
                  aria-label="Remove image"
                  data-testid="image-remove"
                  className="absolute bottom-2 right-2"
                >
                  <TrashIcon className="size-4 fill-cinnabar" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleSelectFile(e.dataTransfer.files?.[0]);
                }}
                className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gainsboro px-4 py-6 text-center hover:border-primary-teal"
                data-testid="image-dropzone"
              >
                <DefaultImageIcon
                  className="size-8 text-grey"
                  title="default-image"
                />
                <span className="text-xs text-txtHeading">
                  Drag &amp; drop file or{" "}
                  <span className="text-blue-500">click to upload</span>
                </span>
                <span className="text-[10px] text-grey">
                  Only support .jpg, .png and .svg (Max 5KB)
                </span>
              </button>
            )}
            <p className="text-[10px] text-grey">
              Recommendation resolution 150&times;150 and max 5KB
            </p>
            {imageError && (
              <p
                className="text-[10px] text-cinnabar"
                data-testid="image-error"
              >
                {imageError}
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              className="hidden"
              onChange={(e) => {
                handleSelectFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              size="sm"
              disabled={!pendingImage}
              onClick={handleUpload}
              className="h-8 rounded-lg text-xs"
              data-testid="image-upload-confirm"
            >
              {pendingImage ? "Upload and Apply" : "Upload"}
            </Button>
          </>
        ) : (
          <>
            {/* Saturation canvas + vertical hue slider on the right */}
            <div className="flex gap-3">
              <div className="flex-1">
                <Saturation
                  hsva={hsva}
                  hue={hsva.h}
                  onChange={(next) => emitHsva({ ...hsva, ...next })}
                  style={{
                    width: "100%",
                    height: CANVAS_HEIGHT,
                    borderRadius: 8
                  }}
                />
              </div>
              <Hue
                hue={hsva.h}
                direction="vertical"
                width={14}
                height={CANVAS_HEIGHT}
                radius={8}
                onChange={(next) => emitHsva({ ...hsva, h: next.h })}
              />
            </div>

            <Tabs value={tab} onValueChange={(v) => setTab(v as ColorTab)}>
              <TabsList className="grid w-full grid-cols-4">
                {COLOR_TABS.map((t) => (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className="text-xs"
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-2">
              <span
                className="size-7 shrink-0 rounded-md border border-gainsboro"
                style={{ backgroundColor: color }}
              />
              {tab === "hex" && (
                <HexInput
                  color={color}
                  onChange={(hex) => onColorChange?.(hex)}
                />
              )}
              {tab === "rgb" && (
                <div className="flex flex-1 gap-2">
                  <ChannelInput
                    label="R"
                    value={rgb.r}
                    max={255}
                    onChange={(r) => emitRgb({ ...rgb, r })}
                  />
                  <ChannelInput
                    label="G"
                    value={rgb.g}
                    max={255}
                    onChange={(g) => emitRgb({ ...rgb, g })}
                  />
                  <ChannelInput
                    label="B"
                    value={rgb.b}
                    max={255}
                    onChange={(b) => emitRgb({ ...rgb, b })}
                  />
                </div>
              )}
              {tab === "hsl" && (
                <div className="flex flex-1 gap-2">
                  <ChannelInput
                    label="H"
                    value={hsl.h}
                    max={360}
                    onChange={(h) =>
                      onColorChange?.(rgbToHex(hslToRgb({ ...hsl, h })))
                    }
                  />
                  <ChannelInput
                    label="S"
                    value={hsl.s}
                    max={100}
                    onChange={(s) =>
                      onColorChange?.(rgbToHex(hslToRgb({ ...hsl, s })))
                    }
                  />
                  <ChannelInput
                    label="L"
                    value={hsl.l}
                    max={100}
                    onChange={(l) =>
                      onColorChange?.(rgbToHex(hslToRgb({ ...hsl, l })))
                    }
                  />
                </div>
              )}
              {tab === "hsb" && (
                <div className="flex flex-1 gap-2">
                  <ChannelInput
                    label="H"
                    value={hsv.h}
                    max={360}
                    onChange={(h) =>
                      onColorChange?.(rgbToHex(hsvToRgb({ ...hsv, h })))
                    }
                  />
                  <ChannelInput
                    label="S"
                    value={hsv.s}
                    max={100}
                    onChange={(s) =>
                      onColorChange?.(rgbToHex(hsvToRgb({ ...hsv, s })))
                    }
                  />
                  <ChannelInput
                    label="B"
                    value={hsv.v}
                    max={100}
                    onChange={(v) =>
                      onColorChange?.(rgbToHex(hsvToRgb({ ...hsv, v })))
                    }
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ColorPickerPanel;
